import * as format from '../../utils/formatters';
import getCurrencySymbol from '../../utils/getCurrencySymbol';
import downloadCSV from '../../utils/downloadCsv';
import Papa from 'papaparse';
import * as sorters from '../../utils/sorters';

export const getColumns = (
    totals,
    currency,
    viewershipType,
    hasInternationalBroadcastPermission,
    t: (key: string, options?: any) => string,
) => {
    const currencyHeaderSuffix = currency
        ? ` (${currency} ${getCurrencySymbol(currency)})`
        : '';
    const columns = [
        {
            Header: t('measurement:broadcastExposure.columns.broadcastName'),
            accessor: 'broadcastName',
            Cell: ({ value }: { value: string }) => format.trimDate(value),
            headerClassName: '--secondary',
            Footer: () => t('measurement:broadcastExposure.table.total'),
            minWidth: 250,
            width: 300,
            format: format.trimDate,
        },
        {
            Header: t('measurement:broadcastExposure.columns.date'),
            accessor: 'broadcastDate',
            Cell: ({ value }: { value: string }) => format.shortDate(value),
            headerClassName: '--secondary',
            minWidth: 100,
            width: 124,
            format: format.shortDate,
        },
        {
            Header: t('measurement:broadcastExposure.columns.day'),
            accessor: 'weekDay',
            sortMethod: (a, b) => sorters.string(a, b),
            Cell: ({ value }: { value: string }) => value,
            headerClassName: '--secondary',
            minWidth: 100,
            width: 124,
            format: value => value || '-',
        },
        {
            Header: t('measurement:broadcastExposure.columns.network'),
            accessor: 'networkName',
            Cell: ({ value }: { value: string }) => value || '-',
            headerClassName: '--secondary',
            minWidth: 150,
            width: 250,
            format: value => value || '-',
        },
        {
            Header: t('measurement:broadcastExposure.columns.viewership', {
                viewershipType: t(
                    `measurement:broadcastExposure.viewershipTypes.${viewershipType}`,
                ),
            }),
            id: 'viewership', // Unique ID for the column
            accessor:
                viewershipType === 'household'
                    ? 'averageViewership'
                    : 'individualViewership', // Dynamically select value based on viewershipType
            Cell: ({ value }: { value: number }) =>
                value ? format.decimal(value) : '-',
            headerClassName: '--secondary',
            Footer: () => format.decimal(totals.viewership),
            minWidth: 165,
            format: value =>
                value
                    ? format.decimal(
                          typeof value === 'string' ? parseFloat(value) : value,
                      )
                    : '-',
        },
        {
            Header: t(
                'measurement:broadcastExposure.columns.exposureImpressions',
            ),
            accessor: 'exposuresImpressions',
            Cell: ({ value }: { value: number }) =>
                value ? format.decimal(value) : '-',
            headerClassName: '--secondary',
            Footer: () => format.decimal(totals.exposuresImpressions),
            minWidth: 165,
            format: value => (value ? format.decimal(value) : '-'),
        },
        {
            Header: `${t(
                'measurement:broadcastExposure.columns.smv',
            )}${currencyHeaderSuffix}`,
            accessor: 'smv',
            Cell: ({ value }: { value: number }) =>
                format.currency(value, { currency }),
            headerClassName: '--secondary',
            Footer: () => format.currency(totals.smv, { currency }),
            minWidth: 140,
            format: value => format.currency(value, { currency }),
        },
    ];

    // Conditionally add the 'Country' column
    if (hasInternationalBroadcastPermission) {
        columns.splice(3, 0, {
            Header: t('measurement:broadcastExposure.columns.country'),
            accessor: 'country',
            Cell: ({ value }: { value: string | number }) =>
                value ? value.toString() : '-',
            headerClassName: '--secondary',
            minWidth: 150,
            format: value => (value ? value.toString() : '-'),
            Footer: null,
        });
    }

    return columns;
};

export const onExportToCsv = (
    data,
    csvDelimiter,
    shouldShowIndividual,
    hasInternationalBroadcastPermission,
    viewershipType: string,
    currency: string | undefined,
    t: (key: string, options?: any) => string,
) => {
    const currencyHeaderSuffix = currency
        ? ` (${currency} ${getCurrencySymbol(currency)})`
        : '';
    const tableData = data.map(row => {
        const viewershipValue =
            viewershipType === 'household'
                ? row.averageViewership
                : row.individualViewership;

        const rowData = {
            [t('measurement:broadcastExposure.columns.broadcastName')]:
                row.broadcastName,
            [t('measurement:broadcastExposure.columns.date')]: format.shortDate(
                row.broadcastDate,
            ),
            [t('measurement:broadcastExposure.columns.day')]: row.weekDay,
            [t('measurement:broadcastExposure.columns.network')]:
                row.networkName,
            [t('measurement:broadcastExposure.columns.viewership', {
                viewershipType: t(
                    `measurement:broadcastExposure.viewershipTypes.${viewershipType}`,
                ),
            })]: viewershipValue,
            [t('measurement:broadcastExposure.columns.exposureImpressions')]:
                row.exposuresImpressions,
            [`${t(
                'measurement:broadcastExposure.columns.smv',
            )}${currencyHeaderSuffix}`]: format.rawNumber(row.smv),
        } as any;

        if (shouldShowIndividual) {
            rowData[
                t('measurement:broadcastExposure.columns.viewership', {
                    viewershipType: t(
                        'measurement:broadcastExposure.viewershipTypes.individual',
                    ),
                })
            ] = row.individualViewership;
        }

        if (hasInternationalBroadcastPermission) {
            rowData[t('measurement:broadcastExposure.columns.country')] =
                row.country;
        }

        return rowData;
    });

    const jsonToCsv = Papa.unparse(tableData, {
        delimiter: csvDelimiter,
    });

    const fileName = 'broadcasts-viewership.csv';
    downloadCSV(jsonToCsv, fileName, false);
};
